/**
 * PreMappingReadinessDrawer — persistent right-side drawer hosting
 * the pre-mapping readiness gate.
 *
 * Why a persistent drawer and not the prior modal:
 *
 *   * Operators frequently need to inspect the workspace canvas
 *     (which source files exist, what mappings already lock, sample
 *     rows) while answering a finding. The modal blocked that — the
 *     canvas was hidden behind the dim layer and any click closed
 *     the dialog. With ``variant="persistent"`` the drawer takes
 *     horizontal space alongside the workspace; clicking nodes /
 *     scrolling the canvas does NOT close it.
 *   * Long resolution sessions (5+ findings, each potentially
 *     needing a transformation expression or lookup-file upload)
 *     are far less stressful when the user can keep their place
 *     while context-switching back to the canvas.
 *
 * Lifecycle owned by ``useMappingWorkflow``:
 *   - ``onClose``         user dismissed the drawer (X / Cancel).
 *   - ``onReadyToStart``  blockers cleared, operator clicked Start;
 *                         the host now triggers the mapping kickoff
 *                         the dialog would have triggered before.
 */
import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, FactCheck as ReadinessIcon } from '@mui/icons-material';

import PreMappingReadinessStep from './PreMappingReadinessStep';

export interface PreMappingReadinessDrawerProps {
  open: boolean;
  batchId: string;
  sessionId?: string;
  /** The schema the operator picked in the Generate Mapping dialog. */
  schemaId: string;
  /** Human-readable schema name shown in the drawer header. */
  schemaName?: string;
  onClose: () => void;
  onReadyToStart: () => void;
}

// 440px is wide enough to fit the resolution forms (text fields,
// candidate radios, transformation editor) without horizontal scroll,
// and narrow enough to leave the workspace canvas usefully visible on
// a typical laptop screen. Matches the width of similar workspace
// drawers (PipelineOutputsDrawer) for visual consistency.
const DRAWER_WIDTH = 440;

export default function PreMappingReadinessDrawer({
  open,
  batchId,
  sessionId,
  schemaId,
  schemaName,
  onClose,
  onReadyToStart,
}: PreMappingReadinessDrawerProps) {
  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      // Persistent drawers do not respond to clicks outside themselves
      // — that's the whole point — so we do NOT pass ``onClose`` here.
      // The X button below is the only dismiss surface.
      ModalProps={undefined}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          // Sit beneath the top app bar so the workspace breadcrumbs
          // stay visible. ``top`` is set in pixels rather than a theme
          // mixin to dodge layout shift while the bar height is being
          // measured on initial paint.
          top: 64,
          height: 'calc(100% - 64px)',
        },
      }}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        // No backdrop — persistent drawers do not render one, but
        // making this explicit guards against future MUI changes.
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <ReadinessIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontSize: 15, fontWeight: 600 }}>
              Check Readiness
            </Typography>
            {schemaName && (
              <Typography variant="caption" color="text.secondary">
                Before mapping to <strong>{schemaName}</strong>
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close readiness drawer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
          {open && (
            // Only mount the step while the drawer is open. The step
            // owns its own report fetch; remounting on open ensures
            // the latest server state is fetched fresh each time the
            // operator returns to the drawer (e.g. after uploading
            // a lookup file from elsewhere).
            <PreMappingReadinessStep
              batchId={batchId}
              sessionId={sessionId}
              targetSchemaId={schemaId}
              onReadyToStart={onReadyToStart}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
